// Adapted from React Bits Particle Text by David Haz.
// https://reactbits.dev/text-animations/particle-text — see LICENSE.md.
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface Particle {
  x: number;
  y: number;
  startX: number;
  startY: number;
  targetX: number;
  targetY: number;
  size: number;
  color: string;
  seed: number;
}

const GATHER_DURATION = 1000;
const STAGGER = 120;
const SCATTER = 45;
const REPEL_RADIUS = 60;
const REPEL_STRENGTH = 8;

export function ParticleText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fallbackRef = useRef<HTMLSpanElement>(null);
  const highlightRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    const fallback = fallbackRef.current;
    const highlight = highlightRef.current;
    if (
      !container ||
      !canvas ||
      !fallback ||
      !highlight ||
      typeof ResizeObserver === 'undefined'
    )
      return;

    const desktop = window.matchMedia('(min-width: 1024px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contrast = window.matchMedia('(prefers-contrast: more)');
    let context: CanvasRenderingContext2D | null = null;
    let particles: Particle[] = [];
    let frame = 0;
    let buildFrame = 0;
    let buildId = 0;
    let width = 0;
    let height = 0;
    let gatherStart = 0;
    let disposed = false;
    const pointer = { active: false, x: 0, y: 0 };

    const canAnimate = () =>
      !disposed &&
      desktop.matches &&
      !reducedMotion.matches &&
      !document.hidden;

    const render = (now: number) => {
      frame = 0;
      if (!context || !canAnimate()) return;
      context.clearRect(0, 0, width, height);
      let moving = false;

      for (const particle of particles) {
        const progress = Math.min(
          1,
          Math.max(
            0,
            (now - gatherStart - particle.seed * STAGGER) / GATHER_DURATION
          )
        );
        const eased = 1 - Math.pow(1 - progress, 3);
        let x = particle.startX + (particle.targetX - particle.startX) * eased;
        let y = particle.startY + (particle.targetY - particle.startY) * eased;

        if (pointer.active) {
          const dx = x - pointer.x;
          const dy = y - pointer.y;
          const distance = Math.hypot(dx, dy);
          if (distance > 0 && distance < REPEL_RADIUS) {
            const force =
              Math.pow(1 - distance / REPEL_RADIUS, 2) * REPEL_STRENGTH;
            x += (dx / distance) * force;
            y += (dy / distance) * force;
          }
        }

        particle.x += (x - particle.x) * 0.22;
        particle.y += (y - particle.y) * 0.22;
        if (progress < 1 || Math.hypot(x - particle.x, y - particle.y) > 0.02) {
          moving = true;
        }
        context.fillStyle = particle.color;
        context.globalAlpha = 0.35 + progress * 0.65;
        context.beginPath();
        context.arc(particle.x, particle.y, particle.size / 2, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
      // Rest once the letters settle; pointer movement wakes the animation.
      if (moving) frame = requestAnimationFrame(render);
    };

    const wake = () => {
      if (!frame && particles.length && canAnimate()) {
        frame = requestAnimationFrame(render);
      }
    };

    const build = async () => {
      const currentBuild = ++buildId;
      if (!canAnimate()) return;
      const rect = container.getBoundingClientRect();
      width = Math.floor(rect.width);
      height = Math.floor(rect.height);
      if (!width || !height) return;

      const computed = getComputedStyle(fallback);
      const font = `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;
      try {
        await document.fonts?.load(font, text);
        if (currentBuild !== buildId || !canAnimate()) return;

        context = canvas.getContext('2d');
        const offscreen = document.createElement('canvas');
        const offContext = offscreen.getContext('2d', {
          willReadFrequently: true,
        });
        if (!context || !offContext) return;

        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        offscreen.width = width;
        offscreen.height = height;
        offContext.font = font;
        offContext.textAlign = 'center';
        offContext.textBaseline = 'alphabetic';
        const metrics = offContext.measureText(text);
        const baseline =
          height / 2 +
          (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) /
            2;
        offContext.fillText(text, width / 2, baseline);
        const pixels = offContext.getImageData(0, 0, width, height).data;

        // Canvas gradients accept resolved CSS colors, including the HSL theme tokens.
        offContext.clearRect(0, 0, width, height);
        const gradient = offContext.createLinearGradient(0, 0, width, 0);
        gradient.addColorStop(0, computed.color);
        gradient.addColorStop(1, getComputedStyle(highlight).color);
        offContext.fillStyle = gradient;
        offContext.fillRect(0, 0, width, 1);
        const colors = offContext.getImageData(0, 0, width, 1).data;
        particles = [];
        // Dense sampling preserves the fine strokes of Richtato's serif wordmark.
        for (let y = 0; y < height; y += 3) {
          for (let x = 0; x < width; x += 3) {
            const alpha = pixels[(y * width + x) * 4 + 3];
            if (alpha <= 40) continue;
            const index = particles.length;
            const seed = ((index * 9301 + 49297) % 233280) / 233280;
            const depth = 0.45 + (((index * 233 + 97) % 1000) / 1000) * 0.9;
            const angle = seed * Math.PI * 2;
            const distance = SCATTER * (0.35 + depth * 0.75);
            const startX = x + Math.cos(angle) * distance;
            const startY = y + Math.sin(angle) * distance;
            particles.push({
              x: startX,
              y: startY,
              startX,
              startY,
              targetX: x,
              targetY: y,
              size: 1.4 * (0.75 + (alpha / 255) * 0.45),
              color: `rgb(${colors[x * 4]}, ${colors[x * 4 + 1]}, ${colors[x * 4 + 2]})`,
              seed,
            });
          }
        }
        if (!particles.length) return;
        gatherStart = performance.now();
        container.dataset.ready = 'true';
        wake();
      } catch {
        // Keep real text visible if fonts or canvas rendering are unavailable.
        delete container.dataset.ready;
      }
    };

    const rebuild = () => {
      buildId += 1;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(buildFrame);
      frame = 0;
      particles = [];
      pointer.active = false;
      delete container.dataset.ready;
      if (canAnimate()) buildFrame = requestAnimationFrame(() => void build());
    };
    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const rect = container.getBoundingClientRect();
      pointer.active = true;
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      wake();
    };
    const onPointerLeave = () => {
      pointer.active = false;
      wake();
    };
    const onVisibilityChange = () => {
      buildId += 1;
      cancelAnimationFrame(frame);
      frame = 0;
      if (document.hidden) return;
      if (particles.length) wake();
      else rebuild();
    };

    const resizeObserver = new ResizeObserver(rebuild);
    resizeObserver.observe(container);
    const themeObserver = new MutationObserver(rebuild);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    desktop.addEventListener('change', rebuild);
    reducedMotion.addEventListener('change', rebuild);
    contrast.addEventListener('change', rebuild);
    document.addEventListener('visibilitychange', onVisibilityChange);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerleave', onPointerLeave);
    rebuild();

    return () => {
      disposed = true;
      buildId += 1;
      cancelAnimationFrame(frame);
      cancelAnimationFrame(buildFrame);
      resizeObserver.disconnect();
      themeObserver.disconnect();
      desktop.removeEventListener('change', rebuild);
      reducedMotion.removeEventListener('change', rebuild);
      contrast.removeEventListener('change', rebuild);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerleave', onPointerLeave);
      delete container.dataset.ready;
    };
  }, [text]);

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label={text}
      className={cn(
        'group relative flex h-56 w-full items-center justify-center overflow-hidden',
        className
      )}
    >
      <span
        ref={fallbackRef}
        aria-hidden="true"
        className="whitespace-nowrap font-serif text-[clamp(3rem,7.5vw,7rem)] font-medium leading-none text-primary group-data-[ready=true]:invisible"
      >
        {text}
      </span>
      <span
        ref={highlightRef}
        aria-hidden="true"
        className="hidden text-sage"
      />
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        className="absolute inset-0 h-full w-full opacity-0 group-data-[ready=true]:opacity-100"
      />
    </div>
  );
}
